// QFinPricer Portal Theme — Clean traditional-finance skin with light/dark support.
//
// Direction: Bloomberg / FactSet / Refinitiv chrome.
//   - Background:  pure white (#FFFFFF) with #F5F6F8 page wash (light)
//                  deep navy (#0F141C) with #1A222E surface (dark)
//   - Text:        near-black (#0F1721) primary, slate-grey (#5B6573) secondary (light)
//                  near-white (#E5E7EB) primary, grey (#9CA3AF) secondary (dark)
//   - Borders:     #E2E5EA thin grey (light) / #2A3441 (dark)
//   - Accent:      ONE slate-blue (#1E3A8A light / #3B5BC7 dark)
//   - Semantics:   green/red kept ONLY for numeric P&L (success.main/error.main)
//   - Shape:       2px radius, no rounded-consumer feel
//
// Replaces the prior BSF dark-teal theme.

import { createTheme, type Theme } from '@mui/material/styles';
import { densityTokens, type Density } from './AppearanceContext';

// ── Type exports ──────────────────────────────────────────────────────────────

export type AppearanceMode = 'light' | 'dark';
export type AppearanceSetting = AppearanceMode | 'auto';

// ── palette.charts TypeScript extension ──────────────────────────────────────

declare module '@mui/material/styles' {
  interface Palette {
    charts: {
      positive: string;     // = success.main; for P&L positive
      negative: string;     // = error.main; for P&L negative
      neutral:  string;     // = text.secondary; for axis labels
      warning:  string;     // = warning.main
      accent:   string;     // = primary.main; for primary line series
      surface:  string;     // background for chart KPI tiles
      gridline: string;     // chart gridlines; subtle ≠ border
      plotly: {
        colorscale: string; // 'Viridis' (light) | 'Plasma' (dark)
        paper:      string; // Plotly paper_bgcolor
        plot:       string; // Plotly plot_bgcolor
      };
    };
  }
  interface PaletteOptions {
    charts?: Partial<Palette['charts']>;
  }
}

// ── Light palette ─────────────────────────────────────────────────────────────

const lightPalette = {
  bg:            '#FFFFFF',
  page:          '#F5F6F8',
  surface:       '#FFFFFF',
  surfaceAlt:    '#FAFBFC',
  surfaceMute:   '#F0F2F5',
  border:        '#E2E5EA',
  borderStrong:  '#CBD0D8',
  textPrimary:   '#0F1721',
  textSecondary: '#5B6573',
  textDisabled:  '#9CA3AF',
  accent:        '#1E3A8A',
  accentHover:   '#1E40AF',
  accentSoft:    '#EEF2FA',
  posNumber:     '#108043',
  negNumber:     '#C8102E',
  warnNumber:    '#B45309',
} as const;

// ── Dark palette ──────────────────────────────────────────────────────────────

const darkPalette = {
  bg:            '#0F141C',
  page:          '#0F141C',
  surface:       '#1A222E',
  surfaceAlt:    '#222B38',
  surfaceMute:   '#2A3441',
  border:        '#2A3441',
  borderStrong:  '#3A4456',
  textPrimary:   '#E5E7EB',
  textSecondary: '#9CA3AF',
  textDisabled:  '#6B7280',
  accent:        '#3B5BC7',
  accentHover:   '#5575D5',
  accentSoft:    '#1E2A4D',
  posNumber:     '#34D399',
  negNumber:     '#F87171',
  warnNumber:    '#FBBF24',
} as const;

// ── Theme factory ─────────────────────────────────────────────────────────────

export function createQfinpricerTheme(
  density: Density = 'compact',
  mode: AppearanceMode = 'light',
): Theme {
  const C = mode === 'dark' ? darkPalette : lightPalette;
  const D = densityTokens(density);

  return createTheme({
    palette: {
      mode,
      primary: {
        main: C.accent,
        light: mode === 'dark' ? '#5575D5' : '#3B5BC7',
        dark:  mode === 'dark' ? '#2A45A0' : '#152A66',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: mode === 'dark' ? '#64748B' : '#475569',
        light: '#64748B',
        dark: '#334155',
        contrastText: '#FFFFFF',
      },
      background: {
        default: C.page,
        paper: C.surface,
      },
      text: {
        primary: C.textPrimary,
        secondary: C.textSecondary,
        disabled: C.textDisabled,
      },
      divider: C.border,
      success: {
        main: C.posNumber,
        light: mode === 'dark' ? '#6EE7B7' : '#34D399',
        dark:  mode === 'dark' ? '#1DAF6E' : '#0C6B36',
      },
      error: {
        main: C.negNumber,
        light: mode === 'dark' ? '#FCA5A5' : '#EF4444',
        dark:  mode === 'dark' ? '#E04040' : '#A50C24',
      },
      warning: {
        main: C.warnNumber,
        light: mode === 'dark' ? '#FCD34D' : '#F59E0B',
        dark:  mode === 'dark' ? '#D97706' : '#8A5208',
      },
      info: {
        main: C.accent,
        light: mode === 'dark' ? '#5575D5' : '#3B5BC7',
        dark:  mode === 'dark' ? '#2A45A0' : '#152A66',
      },
      charts: {
        positive: C.posNumber,
        negative: C.negNumber,
        neutral:  C.textSecondary,
        warning:  C.warnNumber,
        accent:   C.accent,
        surface:  C.surfaceAlt,
        gridline: C.border,
        plotly: {
          colorscale: mode === 'dark' ? 'Plasma' : 'Viridis',
          paper:      mode === 'dark' ? '#1A222E' : '#FFFFFF',
          plot:       mode === 'dark' ? '#0F141C' : '#FAFBFC',
        },
      },
    },
    typography: {
      fontFamily: [
        'Inter',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Tahoma',
        'sans-serif',
      ].join(','),
      h1: { fontSize: '2rem',    fontWeight: 600, letterSpacing: '-0.01em' },
      h2: { fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.01em' },
      h3: { fontSize: '1.5rem',  fontWeight: 600 },
      h4: { fontSize: '1.25rem', fontWeight: 600 },
      h5: { fontSize: '1.05rem', fontWeight: 600 },
      h6: { fontSize: '0.95rem', fontWeight: 600 },
      subtitle1: { fontWeight: 500 },
      body2: { color: C.textSecondary },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    shape: { borderRadius: 2 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // color-scheme on :root hints to the browser to render native UI
          // (date/time picker popups, scrollbars, autofill chrome) in dark.
          // Without this, native <input type="date"> calendar icons stay
          // black-on-dark (invisible) and the picker popup renders white.
          ':root': { colorScheme: mode === 'dark' ? 'dark' : 'light' },
          body: {
            backgroundColor: C.page,
            color: C.textPrimary,
            scrollbarColor: `${C.borderStrong} ${C.page}`,
            '&::-webkit-scrollbar': { width: '8px', height: '8px' },
            '&::-webkit-scrollbar-track': { background: C.page },
            '&::-webkit-scrollbar-thumb': {
              background: C.borderStrong,
              borderRadius: '4px',
            },
          },
          // Make the native calendar-picker indicator (the small button on
          // the right of <input type="date">) readable in dark mode without
          // forcing color-scheme on the input itself (which would override
          // MuiOutlinedInput's wrapper background).
          ...(mode === 'dark' && {
            'input[type="date"]::-webkit-calendar-picker-indicator, input[type="time"]::-webkit-calendar-picker-indicator, input[type="datetime-local"]::-webkit-calendar-picker-indicator':
              { filter: 'invert(1) brightness(0.85)' },
          }),
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 2,
            boxShadow: 'none',
          },
          containedPrimary: {
            backgroundColor: C.accent,
            color: '#FFFFFF',
            '&:hover': { backgroundColor: C.accentHover, boxShadow: 'none' },
          },
          outlinedPrimary: {
            borderColor: C.borderStrong,
            color: C.textPrimary,
            '&:hover': {
              borderColor: C.accent,
              backgroundColor: C.accentSoft,
            },
          },
          textPrimary: {
            color: C.accent,
            '&:hover': { backgroundColor: C.accentSoft },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: C.surface,
            boxShadow: 'none',
            border: `1px solid ${C.border}`,
            borderRadius: 2,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: C.surface,
            boxShadow: 'none',
          },
          elevation1: { boxShadow: 'none', border: `1px solid ${C.border}` },
          elevation2: { backgroundColor: C.surfaceAlt },
          elevation3: { backgroundColor: C.surfaceMute },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: C.surface,
            color: C.textPrimary,
            borderBottom: `1px solid ${C.border}`,
            boxShadow: 'none',
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: { backgroundColor: C.surface },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: C.surface,
            borderRight: `1px solid ${C.border}`,
          },
        },
      },
      MuiTable: {
        defaultProps: { size: D.tableSize },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 600,
              backgroundColor: C.surfaceMute,
              borderBottom: `1px solid ${C.border}`,
              color: C.textPrimary,
              fontSize: D.headerFontSize,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              padding: `${D.cellPaddingY + 2}px ${D.cellPaddingX}px`,
              whiteSpace: 'nowrap',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${C.border}`,
            fontSize: D.bodyFontSize,
            padding: `${D.cellPaddingY}px ${D.cellPaddingX}px`,
          },
          sizeSmall: {
            padding: `${D.cellPaddingY}px ${D.cellPaddingX}px`,
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            '& .MuiTableRow-root:hover': {
              backgroundColor: C.surfaceAlt,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            borderRadius: 2,
            backgroundColor: C.surfaceMute,
            color: C.textPrimary,
            border: `1px solid ${C.border}`,
          },
          colorPrimary: {
            backgroundColor: C.accentSoft,
            color: C.accent,
            borderColor: mode === 'dark' ? '#2A3A6A' : '#C7D2F0',
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small' },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 40 },
          indicator: {
            backgroundColor: C.accent,
            height: 2,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            color: C.textSecondary,
            minHeight: 40,
            '&.Mui-selected': {
              color: C.textPrimary,
            },
            '&:hover': {
              color: C.textPrimary,
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 2,
            '&.Mui-selected': {
              backgroundColor: C.accentSoft,
              borderLeft: `3px solid ${C.accent}`,
              color: C.accent,
              '& .MuiListItemIcon-root': { color: C.accent },
              '&:hover': {
                backgroundColor: mode === 'dark' ? '#253060' : '#E4EAF7',
              },
            },
            '&:hover': {
              backgroundColor: C.surfaceMute,
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: C.border },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: C.textPrimary,
            color: mode === 'dark' ? '#0F141C' : '#FFFFFF',
            borderRadius: 2,
            fontSize: '0.75rem',
          },
          arrow: { color: C.textPrimary },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: C.surface,
            border: `1px solid ${C.border}`,
            backgroundImage: 'none',
            boxShadow: mode === 'dark'
              ? '0 4px 12px rgba(0, 0, 0, 0.4)'
              : '0 4px 12px rgba(15, 23, 33, 0.08)',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: C.surfaceMute },
            '&.Mui-selected': {
              backgroundColor: C.accentSoft,
              '&:hover': { backgroundColor: mode === 'dark' ? '#253060' : '#E4EAF7' },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: { backgroundColor: C.surface },
          icon: { color: C.textSecondary },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: C.surface,
            '& fieldset': { borderColor: C.border },
            '&:hover fieldset': { borderColor: C.borderStrong },
            '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1 },
          },
          input: { color: C.textPrimary },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: C.textSecondary,
            '&.Mui-focused': { color: C.accent },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: C.surface,
            backgroundImage: 'none',
            border: `1px solid ${C.border}`,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            backgroundColor: C.surface,
            borderBottom: `1px solid ${C.border}`,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: C.surface,
            border: `1px solid ${C.border}`,
          },
          listbox: { backgroundColor: C.surface },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 2 },
          standardInfo: {
            backgroundColor: C.accentSoft,
            color: C.accent,
            border: `1px solid ${mode === 'dark' ? '#2A3A6A' : '#C7D2F0'}`,
          },
          standardSuccess: {
            backgroundColor: mode === 'dark' ? '#0E2E20' : '#ECFDF3',
            color: C.posNumber,
            border: `1px solid ${mode === 'dark' ? '#1A4A30' : '#BBEAD0'}`,
          },
          standardWarning: {
            backgroundColor: mode === 'dark' ? '#2E1E00' : '#FFF7ED',
            color: C.warnNumber,
            border: `1px solid ${mode === 'dark' ? '#4A3000' : '#FCE2B5'}`,
          },
          standardError: {
            backgroundColor: mode === 'dark' ? '#2E0F0F' : '#FEF2F2',
            color: C.negNumber,
            border: `1px solid ${mode === 'dark' ? '#4A1A1A' : '#F4C6CD'}`,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: C.textSecondary,
            '&:hover': {
              backgroundColor: C.surfaceMute,
              color: C.textPrimary,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: C.borderStrong,
            '&.Mui-checked': { color: C.accent },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: C.borderStrong,
            '&.Mui-checked': { color: C.accent },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          track: { backgroundColor: C.borderStrong },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { backgroundColor: C.surfaceMute },
          bar: { backgroundColor: C.accent },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: { backgroundColor: mode === 'dark' ? '#2A3441' : '#EDEFF3' },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          separator: { color: C.textDisabled },
          li: { color: C.textSecondary },
        },
      },
    },
  });
}

// Default compact-density light theme — used at module load before the AppearanceProvider
// mounts (e.g. SSR or initial paint). At runtime App.tsx re-creates the theme
// keyed on the user's saved density + mode preference.
const qfinpricerTheme = createQfinpricerTheme('compact', 'light');

export default qfinpricerTheme;
